import * as stylex from "@stylexjs/stylex";
import { X } from "lucide-react";
import { type ReactNode, useId, useRef } from "react";
import "@/theme/tokens.stylex";
import { colors, motion, radii, shadows, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { dialogKeyDown, useFocusOnOpen } from "./dialogFocus";
import { Eyebrow } from "./Eyebrow";
import { useFieldInView } from "./fieldInView";
import { IconButton } from "./IconButton";
type SheetProps = {
  title: string;
  eyebrow?: ReactNode;
  closeLabel: string;
  onClose: () => void;
  onScrimTap?: () => void;
  children: ReactNode;
  startActions?: ReactNode;
  endActions?: ReactNode;
  keyboardInset?: number;
  uniform?: boolean;
  hideTitle?: boolean;
  placement?: SheetPlacement;
  xstyle?: stylex.StyleXStyles;
};
export type SheetPlacement = "center" | "top" | "full";
const PAD = "clamp(24px, 3vw, 40px)";
const styles = {
  overlay: {
    kVAEAm: "xixxii4",
    kpwlN0: "x10a8y8t",
    kY2c9j: "x1n327nk",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kmVPX3: "x18ppelm",
    kB7OPa: "x9f619",
    k1ekBW: "xgkp4zd",
    kIyJzY: "x1fmhw06",
    kAMwcw: "x1em5f5i",
    $$css: true
  },
  aboveKeyboard: (inset: number) => [{
    krVfgx: inset != null ? "x1nqzi6q" : inset,
    $$css: true
  }, {
    "--x-bottom": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(inset)
  }],
  overlayTop: {
    kgQiWS: "x1knln2f",
    kLKAdn: "x14cexlh",
    $$css: true
  },
  overlayFull: {
    kgQiWS: "x13b6k1y",
    kmVPX3: "x1717udv",
    $$css: true
  },
  card: {
    kVAEAm: "x1n2onr6",
    kzqmXN: "xh8yej3",
    ks0D6T: "x27kpxv",
    kskxy: "xmz0i5r",
    kVQacm: "xb3r6kr",
    kB7OPa: "x9f619",
    kaIpWk: "x350i8",
    kWkggS: "x5r8ks9",
    kMwMTN: "x1a11ngh",
    kGVxlE: "xs0yqle",
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kI3sdo: "x1a2a7pz",
    $$css: true
  },
  cardTop: {
    ks0D6T: "x11gisft",
    $$css: true
  },
  cardFull: {
    ks0D6T: "x1x1rfll",
    kZKoxP: "x5yr21d",
    kaIpWk: "x2u8bby",
    $$css: true
  },
  eyebrow: {
    k1xSpc: "x78zum5",
    kwnvtZ: "x1a02dak",
    kGNEyG: "x6s0dn4",
    k1C7PZ: "xtqikln",
    kAzted: "xisnujt",
    $$css: true
  }
};
export function Sheet({
  title,
  eyebrow,
  closeLabel,
  onClose,
  onScrimTap = onClose,
  children,
  startActions,
  endActions,
  keyboardInset = 0,
  uniform = false,
  hideTitle = false,
  placement = "center",
  xstyle
}: SheetProps) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  const body = useRef<HTMLDivElement>(null);
  useFocusOnOpen(dialog);
  useFieldInView(body, keyboardInset);
  return <div {...stylex.props(styles.overlay, styles.aboveKeyboard(keyboardInset), placement === "top" && styles.overlayTop, placement === "full" && styles.overlayFull)}>
      <button type="button" tabIndex={-1} aria-hidden="true" onClick={onScrimTap} className="x10l6tqk x10a8y8t xc342km x1717udv x1ebrkjs" />
      <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} onKeyDown={dialogKeyDown(onClose)} {...stylex.props(styles.card, xstyle, placement === "top" && styles.cardTop, placement === "full" && styles.cardFull)}>
        <div {...{
        0: {
          className: "x1n2onr6 x1vjfegm x2lah0s x78zum5 x1qughib x1cy8zhl xddi9bj xl76xaq x1eq97yh xsag5q8 xh540kj xq9ty8n"
        },
        2: {
          className: "x1n2onr6 x1vjfegm x2lah0s x78zum5 x1qughib x1cy8zhl xddi9bj xl76xaq x1eq97yh xsag5q8 xgcd1z6 x1gnnqk1"
        },
        1: {
          className: "x1n2onr6 x1vjfegm x2lah0s x78zum5 x1qughib xddi9bj xl76xaq x1eq97yh xsag5q8 xh540kj xq9ty8n x6s0dn4"
        },
        3: {
          className: "x1n2onr6 x1vjfegm x2lah0s x78zum5 x1qughib xddi9bj xl76xaq x1eq97yh xsag5q8 xgcd1z6 x1gnnqk1 x6s0dn4"
        }
      }[!!uniform << 1 | !!hideTitle << 0]}>
          <div className="x78zum5 xdt5ytf x17d4w8g">
            {eyebrow && <Eyebrow xstyle={styles.eyebrow}>{eyebrow}</Eyebrow>}
            <h2 id={titleId} {...{
            0: {
              className: "x1ghz6dp xuaujqh x182gq9d xipi2l9 x1159mfc"
            },
            1: {
              className: "x1ghz6dp xuaujqh x182gq9d xipi2l9 x1159mfc x10l6tqk x1i1rx1s xjm9jq1 xb3r6kr x1hyvwdk xuxw1ft"
            }
          }[!!hideTitle << 0]}>
              {title}
            </h2>
          </div>
          <IconButton icon={X} label={closeLabel} size="touch" onClick={onClose} />
        </div>
        <div ref={body} data-sheet-body {...{
        0: {
          className: "x1iyjqo2 x2lwn1j x1odjw0f x78zum5 xdt5ytf x1c1vhfx xqp6ajr x1eq97yh"
        },
        1: {
          className: "x2lwn1j x1odjw0f x78zum5 xdt5ytf x1c1vhfx xqp6ajr x1eq97yh x1c4vz4f"
        }
      }[!!(placement === "full") << 0]}>
          {children}
        </div>
        {(startActions || endActions) && <div {...{
        0: {
          className: "x1n2onr6 x1vjfegm x2lah0s xyamay9 x1eq97yh x17du0tb xh540kj xuh0kgx x78zum5 x1a02dak x1qughib x6s0dn4 x1hu4l6k"
        },
        1: {
          className: "x1n2onr6 x1vjfegm x2lah0s xyamay9 x1eq97yh x17du0tb x78zum5 x1a02dak x1qughib x6s0dn4 x1hu4l6k xgcd1z6 x1gnnqk1"
        }
      }[!!uniform << 0]}>
            <div className="x78zum5 x152posx">{startActions}</div>
            <div className="x78zum5 x152posx x8x9d4c">{endActions}</div>
          </div>}
      </div>
    </div>;
}